import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import TableTemplate from "../../../../../Components/Table/TableTemplate";
import { SCHOOLS_ENDPOINT } from "../../../../../Constants/Endpoints";
import {
  activateAdviceSchoolRequest,
  changeAllSchoolBookStatusParams,
  changeSchoolBookStatusParams,
} from "../../../../../Backend/SchoolsRequests";
import { objectToQueryString } from "../../../../../Helpers/utils/utils";
import { ErrorLevels } from "../../../../../Constants";
import { setToastText } from "../../../../../Reducer";
import { ManageStatusModal } from "./ManageStatusModal/ManageStatusModal";
import "./SchoolOverviewPage.scss";

export function SchoolOverviewPage() {
  const dispatch = useDispatch();
  const tableTemplateRef = useRef(null);
  const [t] = useTranslation();
  const [showManageStatusModal, setShowManageStatusModal] = useState(false);

  const [columns] = useState([
    {
      name: "schoolNumber",
      headerCaption: t("SchoolNumber"),
      size: "normal",
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "schoolDeliveryType",
      headerCaption: t("SchoolType"),
      size: "normal",
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "name",
      headerCaption: t("Name"),
      size: "large",
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "studentCount",
      headerCaption: t("Students"),
      canSort: true,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "adminBookStatusFinished",
      headerCaption: t("AdminBookStatusFinished"),
      size: "small",
      textAlign: "center",
      canSort: false,
      canFilter: false,
      canEdit: false,
      caption: (x) => (
        <span className="is-checkbox">
          {x.adminBookStatusFinished ? "check_box" : "check_box_outline_blank"}
        </span>
      ),
    },
    {
      name: "bookStatusFinished",
      headerCaption: t("BookStatusFinished"),
      size: "small",
      textAlign: "center",
      canSort: false,
      canFilter: false,
      canEdit: false,
      caption: (x) => (
        <span className="is-checkbox">
          {x.bookStatusFinished ? "check_box" : "check_box_outline_blank"}
        </span>
      ),
    },
  ]);

  const enableNotification = async (id) => {
    dispatch(
      setToastText({
        text: t("EnablingNotifications") + "...",
        level: ErrorLevels.Information,
      })
    );
    const resp = await activateAdviceSchoolRequest(
      objectToQueryString({ schoolId: id }, true)
    );
    if (resp.success) {
      dispatch(
        setToastText({
          text: t("NotificationEnabled"),
          level: ErrorLevels.Success,
        })
      );
    } else {
      dispatch(
        setToastText({
          text: t("NotificationEnabledFailed"),
          level: ErrorLevels.Error,
        })
      );
    }
  };

  const toggleCompleteAdminBookStatus = async (school) => {
    const params = {
      schoolId: school.id,
      status: school.bookStatusFinished,
      adminStatus: !school.adminBookStatusFinished,
    };

    const resp = await changeSchoolBookStatusParams(
      objectToQueryString(params, true)
    );

    if (resp.success) {
      tableTemplateRef.current.getItems();
    }
  };

  const allSchoolsCompleteAdminBookStatus = async (adminStatus) => {
    const params = {
      adminStatus: adminStatus,
    };
    const resp = await changeAllSchoolBookStatusParams(
      objectToQueryString(params, true)
    );
    if (resp.success) {
      tableTemplateRef.current.getItems();
      setShowManageStatusModal(false);
    }
  };

  return (
    <div className="school-overview-page">
      <TableTemplate
        ref={tableTemplateRef}
        title={t("SchoolsOverview")}
        endpoint={SCHOOLS_ENDPOINT}
        columns={columns}
        uniqueId="schoolNumber"
        paginator={true}
        customButtons={[
          {
            name: t("ManageAllBookStatus"),
            action: () => setShowManageStatusModal(true),
          },
        ]}
        customActions={[
          {
            name: (row) =>
              row.adminBookStatusFinished
                ? t("DisableAdminBookStatus")
                : t("EnableAdminBookStatus"),
            action: (row) => toggleCompleteAdminBookStatus(row),
          },
          {
            name: t("EnableNotification"),
            action: (row) => enableNotification(row.id),
          },
        ]}
      />
      {showManageStatusModal && (
        <ManageStatusModal
          show={showManageStatusModal}
          submit={(adminStatus) =>
            allSchoolsCompleteAdminBookStatus(adminStatus)
          }
          onHide={() => setShowManageStatusModal(false)}
        />
      )}
    </div>
  );
}
